// REQ-P1-06b: お知らせContextの契約型付きAPI。

import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type AnnouncementFeedQuery = NonNullable<
  paths['/announcements/feed']['get']['parameters']['query']
>;
type AnnouncementFeedResponse = NonNullable<
  paths['/announcements/feed']['get']['responses'][200]['content']
>['application/json'];
type AnnouncementAcknowledgeResponse = NonNullable<
  paths['/announcements/{id}/acknowledge']['post']['responses'][200]['content']
>['application/json'];
type AnnouncementDismissRequest = NonNullable<
  paths['/announcements/dismiss']['post']['requestBody']
>['content']['application/json'];
type AnnouncementDismissResponse = NonNullable<
  paths['/announcements/dismiss']['post']['responses'][200]['content']
>['application/json'];
type AdminAnnouncementPendingCountResponse = NonNullable<
  paths['/admin/announcements/pending-count']['get']['responses'][200]['content']
>['application/json'];
type AdminAnnouncementListQuery = NonNullable<
  paths['/admin/announcements']['get']['parameters']['query']
>;
type AdminAnnouncementListResponse = NonNullable<
  paths['/admin/announcements']['get']['responses'][200]['content']
>['application/json'];
type AdminAnnouncementMutationRequest = NonNullable<
  paths['/admin/announcements']['post']['requestBody']
>['content']['application/json'];
type AdminAnnouncementApproveRequest = NonNullable<
  paths['/admin/announcements/{id}/approve']['post']['requestBody']
>['content']['application/json'];

function ensureData<T>(data: T | undefined, status: number, ok: boolean): T {
  if (!ok || !data) throw new ApiStatusError(status);
  return data;
}

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwApiError(status: number, error: unknown, fallbackMessage: string): never {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage;
  throw statusError;
}

export async function listAnnouncementFeed(
  query: AnnouncementFeedQuery
): Promise<AnnouncementFeedResponse> {
  const { data, response } = await api.GET('/announcements/feed', {
    params: { query },
  });
  return ensureData(data as AnnouncementFeedResponse | undefined, response.status, response.ok);
}

export async function acknowledgeAnnouncement(
  id: number
): Promise<AnnouncementAcknowledgeResponse> {
  const { data, response } = await api.POST('/announcements/{id}/acknowledge', {
    params: { path: { id } },
  });
  return ensureData(data as AnnouncementAcknowledgeResponse | undefined, response.status, response.ok);
}

export async function dismissAnnouncements(
  announcementIds: AnnouncementDismissRequest['announcementIds']
): Promise<AnnouncementDismissResponse> {
  const { data, response } = await api.POST('/announcements/dismiss', {
    body: { announcementIds },
  });
  return ensureData(data as AnnouncementDismissResponse | undefined, response.status, response.ok);
}

export async function getAdminAnnouncementPendingCount(): Promise<AdminAnnouncementPendingCountResponse> {
  const { data, response } = await api.GET('/admin/announcements/pending-count');
  return ensureData(data as AdminAnnouncementPendingCountResponse | undefined, response.status, response.ok);
}

export async function listAdminAnnouncements(
  query: AdminAnnouncementListQuery
): Promise<AdminAnnouncementListResponse> {
  const { data, error, response } = await api.GET('/admin/announcements', {
    params: { query },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'お知らせ一覧の取得に失敗しました');
  }
  return data as AdminAnnouncementListResponse;
}

export async function createAdminAnnouncement(
  body: AdminAnnouncementMutationRequest
): Promise<void> {
  const { error, response } = await api.POST('/admin/announcements', { body });
  if (!response.ok) {
    throwApiError(response.status, error, '保存に失敗しました');
  }
}

export async function updateAdminAnnouncement(
  id: number,
  body: AdminAnnouncementMutationRequest
): Promise<void> {
  const { error, response } = await api.PUT('/admin/announcements/{id}', {
    params: { path: { id } },
    body,
  });
  if (!response.ok) {
    throwApiError(response.status, error, '保存に失敗しました');
  }
}

export async function approveAdminAnnouncement(
  id: number,
  body: AdminAnnouncementApproveRequest
): Promise<void> {
  const { error, response } = await api.POST('/admin/announcements/{id}/approve', {
    params: { path: { id } },
    body,
  });
  if (!response.ok) {
    throwApiError(response.status, error, '操作に失敗しました');
  }
}

export async function archiveAdminAnnouncement(id: number): Promise<void> {
  const { error, response } = await api.POST('/admin/announcements/{id}/archive', {
    params: { path: { id } },
  });
  if (!response.ok) {
    throwApiError(response.status, error, '操作に失敗しました');
  }
}
